import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor, { sanitizeNoteHtml } from "@/components/RichTextEditor";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  PaperPlaneRight, Hash, PushPin, PushPinSlash, Trash, MagnifyingGlass, CheckSquare,
  PencilSimple, X, Check, ClockCounterClockwise, Paperclip, ArrowSquareOut, Download, FilePdf, Eye,
} from "@phosphor-icons/react";

const ALL_TAB = "__all__";
const HASHTAG_RE = /#([A-Za-z0-9_-]{1,40})/g;
const PAGE_SIZE = 25;

function fmtTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today, ${time}`;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate()
  ) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${time}`;
}

function initialsFor(name) {
  return (name || "?").trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

function renderBody(note) {
  // If body_html exists, render sanitized HTML (Bold/Italic/Underline/Strike).
  // Else fall back to plain body with inline hashtag highlighting.
  const html = note?.body_html;
  const body = note?.body || "";
  if (html && html.trim()) {
    // Highlight hashtags inside the rendered HTML by string replace on the
    // sanitized output — DOMPurify already stripped anything unsafe.
    const withTags = String(html).replace(HASHTAG_RE, (_m, tag) => (
      `<span style="color:#1f64c2;font-weight:600">#${tag}</span>`
    ));
    return <span dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(withTags) }} />;
  }
  // Plain-text back-compat path
  const parts = [];
  let last = 0;
  let m;
  HASHTAG_RE.lastIndex = 0;
  while ((m = HASHTAG_RE.exec(body)) !== null) {
    if (m.index > last) parts.push({ t: "txt", v: body.slice(last, m.index) });
    parts.push({ t: "tag", v: m[1] });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ t: "txt", v: body.slice(last) });
  return parts.map((p, i) =>
    p.t === "tag"
      ? <span key={i} className="text-[#1f64c2] font-semibold">#{p.v}</span>
      : <span key={i} style={{ whiteSpace: "pre-wrap" }}>{p.v}</span>
  );
}

export default function NotesPanel({ refreshHeader }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(ALL_TAB); // category name OR ALL_TAB
  const [search, setSearch] = useState(""); // hashtag query
  const [categories, setCategories] = useState([]);
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [posting, setPosting] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editBodyHtml, setEditBodyHtml] = useState("");
  const [savingEditId, setSavingEditId] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  // Attachment state for the composer
  const [pendingAttach, setPendingAttach] = useState(null); // { file_id, filename, content_type, size, mode }
  const [attachModalFile, setAttachModalFile] = useState(null); // freshly uploaded file awaiting reference/preview choice
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [composerDrag, setComposerDrag] = useState(false);
  const [viewAttach, setViewAttach] = useState(null); // attachment object to preview inline
  const fileInputRef = useRef(null);
  // Bulk-select mode (super admin only)
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'pin' | 'delete'
  // Categories management
  const [editCatsMode, setEditCatsMode] = useState(false);
  const [archivedCats, setArchivedCats] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [tombstones, setTombstones] = useState([]);
  const [renamingCat, setRenamingCat] = useState(null); // {name, newName}
  const [confirmDelCat, setConfirmDelCat] = useState(null);
  // Individual delete confirmation
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null); // note object
  // Note-group support
  const [group, setGroup] = useState("general"); // active pill: general | permissioned | private
  const [access, setAccess] = useState(null); // { visible_groups, can_global_search, is_super_admin, can_post }
  const [composerGroup, setComposerGroup] = useState("general");
  const [groupChangeTarget, setGroupChangeTarget] = useState(null); // note object
  const [globalQuery, setGlobalQuery] = useState("");
  const [globalHits, setGlobalHits] = useState(null); // null | array
  const [globalLoading, setGlobalLoading] = useState(false);
  // jump_to_feed — when the user clicks a hit in the global search overlay
  // OR the "jump to feed" pill on a filtered-feed note, we clear filters,
  // switch to the note's group, then scroll to & briefly highlight the row
  // once it appears in the feed. ``pendingJumpId`` triggers the scroll
  // effect below; ``highlightId`` drives the ring animation.
  const [pendingJumpId, setPendingJumpId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const jumpAttemptsRef = useRef(0);
  // Undo-delete state — tracks the most recent deletion (bulk OR individual)
  // so we can show a pale-red Undo pill under "Mark all as read". Cleared on
  // page refresh (no persistence) or when the undo button is pressed.
  const [lastDelete, setLastDelete] = useState(null); // { ids: [], label: '' }
  // Freeze the user's last read cursor at MOUNT so newly-arrived notes stay
  // highlighted in pale yellow for at least 10 s after the panel opens — gives
  // the human a chance to actually see what they missed instead of having the
  // highlight vanish immediately.
  const [frozenCursor] = useState(user?.last_notes_read_at || user?.created_at || "");
  const [unreadActive, setUnreadActive] = useState(true);
  const sentinelRef = useRef(null);
  const composerRef = useRef(null);

  const activeHashtag = useMemo(() => search.trim().replace(/^#/, "").toLowerCase(), [search]);
  const activeCategory = tab === ALL_TAB ? null : tab;

  const loadCategories = useCallback(async () => {
    try {
      const [c, a, t] = await Promise.all([
        api.get("/notes/categories"),
        api.get("/notes/categories/archived"),
        api.get("/notes/categories/tombstones"),
      ]);
      setCategories(c.data || []);
      setArchivedCats(a.data || []);
      setTombstones(t.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadNotes = useCallback(async ({ append = false } = {}) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, group };
      if (append && cursor) params.cursor = cursor;
      if (activeHashtag) params.hashtag = activeHashtag;
      if (activeCategory) params.category = activeCategory;
      const { data } = await api.get("/notes", { params });
      setNotes((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setLoading(false);
    }
  }, [cursor, activeHashtag, activeCategory, group]);

  // Fetch the caller's note-group access + defaults on mount / user change.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notes/access");
        setAccess(data);
        const visible = data?.visible_groups || ["general"];
        // Reset the pill selector to a group the user CAN see.
        setGroup((g) => (visible.includes(g) ? g : visible[0] || "general"));
        setComposerGroup((g) => (visible.includes(g) ? g : visible[0] || "general"));
      } catch {
        setAccess({ visible_groups: ["general"], can_global_search: false, is_super_admin: false, can_post: false });
      }
    })();
  }, [user?.id]);

  // Initial + filter changes
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    // Reset list on filter change
    setNotes([]);
    setCursor(null);
    setHasMore(false);
    loadNotes({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHashtag, activeCategory, group]);

  // Wait 10 s before marking notes as read — this lets the user actually SEE
  // the pale-yellow unread highlight on freshly arrived notes (otherwise it
  // would flash and disappear on page load). After the delay we call
  // /notes/read-all (which advances the server-side cursor) and toggle
  // ``unreadActive=false`` to let CSS transition the bubble back to its
  // normal pale-blue.
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/notes/read-all")
        .then(() => refreshHeader && refreshHeader())
        .catch(() => {});
      setUnreadActive(false);
    }, 10000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite-scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadNotes({ append: true });
      }
    }, { rootMargin: "200px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, loadNotes]);

  const post = async () => {
    const text = body.trim();
    if (!text) return;
    setPosting(true);
    try {
      const payload = { body: text, body_html: sanitizeNoteHtml(bodyHtml) || null, group: composerGroup };
      if (pendingAttach) {
        payload.attachment_file_id = pendingAttach.file_id;
        payload.attachment_mode = pendingAttach.mode || "reference";
      }
      const { data: created } = await api.post("/notes", payload);
      setBody("");
      setBodyHtml("");
      setPendingAttach(null);
      // Prepend to the feed (only if filters allow it to appear)
      const matchesHashtag = !activeHashtag || (created.hashtags || []).includes(activeHashtag);
      const matchesCategory = !activeCategory || created.pinned_category === activeCategory;
      const matchesGroup = created.group === group;
      if (matchesHashtag && matchesCategory && matchesGroup) setNotes((prev) => [created, ...prev]);
      refreshHeader && refreshHeader();
      composerRef.current?.focus();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setPosting(false);
    }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024 * 1024) { toast.error("File exceeds 5 GB limit"); return; }
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/notes/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns both ``id`` and ``file_id`` — normalize on file_id so
      // every downstream consumer (composer chip, post body, edit dialog)
      // sees the same shape used by ``note.attachment``.
      const fileObj = { ...data, file_id: data.file_id || data.id };
      // Open modal so user picks reference vs preview before posting.
      setAttachModalFile(fileObj);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setUploadingAttach(false);
    }
  };

  const onComposerDrop = (e) => {
    e.preventDefault();
    setComposerDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadAttachment(f);
  };

  // Paste-to-attach: when the user pastes a file/image/PDF from their
  // clipboard into the composer, treat it like a drag-drop and route it
  // through the same upload + mode-chooser modal. Plain text pastes fall
  // through to default behaviour.
  const onComposerPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          uploadAttachment(f);
          return;
        }
      }
    }
    // No file in clipboard — let text paste through normally.
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl + Enter to post
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      post();
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notes/read-all");
      toast.success("Notes marked as read");
      refreshHeader && refreshHeader();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const onPin = (note) => setPinTarget(note);
  const onUnpin = async (note) => {
    try {
      await api.post(`/notes/${note.id}/unpin`);
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, pinned_category: null } : n)));
      loadCategories();
      toast.success("Note unpinned");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const onDelete = (note) => setConfirmDeleteNote(note);

  const confirmIndividualDelete = async () => {
    const note = confirmDeleteNote;
    if (!note) return;
    try {
      await api.delete(`/notes/${note.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      setLastDelete({ ids: [note.id], label: "1 note deleted" });
      toast.success("Note deleted");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setConfirmDeleteNote(null);
    }
  };

  const undoLastDelete = async () => {
    if (!lastDelete?.ids?.length) return;
    try {
      const { data } = await api.post("/notes/undo-delete", { note_ids: lastDelete.ids });
      toast.success(`Restored ${data.restored} note${data.restored === 1 ? "" : "s"}`);
      setLastDelete(null);
      // Reload the feed so the restored bubble(s) reappear in the right place.
      loadNotes({ append: false });
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditBody(note.body || "");
    setEditBodyHtml(note.body_html || sanitizeNoteHtml((note.body || "").replace(/\n/g, "<br>")));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
    setEditBodyHtml("");
  };

  const saveEdit = async (note) => {
    const nextHtml = sanitizeNoteHtml(editBodyHtml);
    const tmp = document.createElement("div");
    tmp.innerHTML = nextHtml;
    const nextText = (tmp.innerText || tmp.textContent || "").trim();
    if (!nextText) { toast.error("Note cannot be empty"); return; }
    if (nextText === (note.body || "").trim() && nextHtml === (note.body_html || "")) { cancelEdit(); return; }
    setSavingEditId(note.id);
    try {
      const { data } = await api.put(`/notes/${note.id}`, { body: nextText, body_html: nextHtml || null });
      setNotes((prev) => prev.map((n) => (n.id === note.id ? data : n)));
      setEditingId(null);
      setEditBody("");
      setEditBodyHtml("");
      toast.success("Note updated");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setSavingEditId(null);
    }
  };

  const visibleGroups = access?.visible_groups || ["general"];
  const canGlobalSearch = !!access?.can_global_search;
  const canSeePrivate = !!access?.is_super_admin;
  const cantSeeCurrent = !visibleGroups.includes(group);

  const runGlobalSearch = async (q) => {
    const needle = (q ?? globalQuery).trim();
    if (!needle) { setGlobalHits(null); return; }
    setGlobalLoading(true);
    try {
      const { data } = await api.get("/notes/search", { params: { q: needle } });
      setGlobalHits(data?.items || []);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      setGlobalHits([]);
    } finally { setGlobalLoading(false); }
  };

  // jump_to_feed — called when the user clicks any search hit (global bar
   // or hashtag/keyword bar). Clears filters, switches to the note's group
   // if needed, closes the global overlay, and sets ``pendingJumpId`` so
   // the effect below can scroll+highlight once the note lands in ``notes``.
   const jumpToNote = useCallback((note) => {
    if (!note?.id) return;
    const targetGroup = note.group || "general";
    // Close global overlay and clear per-feed filters.
    setGlobalHits(null);
    setGlobalQuery("");
    setSearch("");
    setTab(ALL_TAB);
    jumpAttemptsRef.current = 0;
    setPendingJumpId(note.id);
    // Switching group triggers a feed reload (see filter effect).
    if (targetGroup !== group) setGroup(targetGroup);
  }, [group]);

  // Watch ``notes`` for the pending target. If found → scroll + highlight.
  // If not present and more pages exist → auto-load more (up to 5 attempts).
  useEffect(() => {
    if (!pendingJumpId) return;
    const el = document.querySelector(`[data-note-id="${pendingJumpId}"]`);
    if (el) {
      // Wait a tick so any layout shifts from the fresh load settle first.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(pendingJumpId);
        setPendingJumpId(null);
        setTimeout(() => setHighlightId(null), 1800);
      });
      return;
    }
    // Not found yet — page in more if available.
    if (hasMore && !loading && jumpAttemptsRef.current < 5) {
      jumpAttemptsRef.current += 1;
      loadNotes({ append: true });
    } else if (!hasMore && !loading) {
      toast.error("The matching note is no longer visible in this group.");
      setPendingJumpId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingJumpId, notes, hasMore, loading]);

  const changeNoteGroup = async (noteId, targetGroup) => {    try {
      const { data } = await api.put(`/notes/${noteId}/group`, { group: targetGroup });
      // Note moved out of current view — drop it from the list.
      if (data.group !== group) setNotes((prev) => prev.filter((n) => n.id !== noteId));
      else setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...data } : n)));
      toast.success(`Moved to ${targetGroup} notes`);
      setGroupChangeTarget(null);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  return (
    <div className="space-y-4" data-testid="notes-panel">
      {/* Global search bar — only super admin + management can use it. */}
      {canGlobalSearch && (
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={globalQuery}
            onChange={(e) => setGlobalQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runGlobalSearch(); if (e.key === "Escape") { setGlobalQuery(""); setGlobalHits(null); } }}
            placeholder="Global Search — #hashtags OR any keyword across your note groups"
            className="pl-9 pr-24 bg-white border-2 border-[#327de3]/30 focus-visible:border-[#327de3]"
            data-testid="notes-global-search"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {globalHits !== null && (
              <Button variant="ghost" size="sm" onClick={() => { setGlobalQuery(""); setGlobalHits(null); }} className="h-7 text-[11px] px-2" data-testid="notes-global-clear">
                Clear
              </Button>
            )}
            <Button size="sm" onClick={() => runGlobalSearch()} className="h-7 px-3 bg-[#327de3] hover:bg-[#1f64c2]" data-testid="notes-global-search-btn">
              Search
            </Button>
          </div>
        </div>
      )}

      {/* Global search results (overlay). Hide the main feed when active. */}
      {globalHits !== null ? (
        <div className="rounded-lg border border-[#327de3]/30 bg-white p-3" data-testid="notes-global-results">
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
            {globalLoading ? "Searching…" : `${globalHits.length} result${globalHits.length === 1 ? "" : "s"} across ${visibleGroups.join(" · ")}`}
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {globalHits.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jumpToNote(r)}
                className="block w-full text-left rounded-md border border-slate-200 bg-white px-3 py-2 hover:border-[#327de3] hover:bg-[#eaf2ff] transition-colors"
                data-testid={`notes-global-hit-${r.id}`}
                title="Jump to this note in the feed"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  <span className={`px-1.5 py-0.5 rounded ${r.group === "private" ? "bg-rose-100 text-rose-700" : r.group === "permissioned" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.group || "general"}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-600 normal-case tracking-normal">{r.author_name || r.author_email}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500 normal-case tracking-normal">{fmtTimestamp(r.created_at)}</span>
                  <span className="ml-auto text-[#1f64c2] normal-case tracking-normal font-semibold">Jump to feed →</span>
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.body}</div>
              </button>
            ))}
            {!globalLoading && globalHits.length === 0 && (
              <div className="text-xs text-slate-400 italic py-6 text-center">No matches.</div>
            )}
          </div>
        </div>
      ) : (
      <>
      {/* Group pill selector — General / Permissioned / Private */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="notes-group-pills">
        <GroupPill active={group === "general"} onClick={() => setGroup("general")} testId="notes-group-general">General Notes</GroupPill>
        <GroupPill active={group === "permissioned"} onClick={() => setGroup("permissioned")} testId="notes-group-permissioned">Permissioned Notes</GroupPill>
        {canSeePrivate && (
          <GroupPill active={group === "private"} onClick={() => setGroup("private")} testId="notes-group-private" tone="private">Private Notes</GroupPill>
        )}
      </div>

      {/* Gate: current pill is not accessible for this user */}
      {cantSeeCurrent && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-6 text-center" data-testid="notes-group-gate">
          <div className="text-sm text-amber-900 font-semibold">Access required</div>
          <div className="text-xs text-amber-800 mt-1">Request access from your administrator for {group} notes.</div>
        </div>
      )}

      {!cantSeeCurrent && (
      <>
      {/* Header: search + mark-all-read */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by #hashtag or any keyword — e.g. #release or vacation"
            className="pl-9 bg-white"
            data-testid="notes-hashtag-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Bulk toggle — visible to every notes-permissioned user. Super
              admins get both pin + delete; others get pin only. */}
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setBulkMode((m) => !m);
              setBulkSelected(new Set());
            }}
            data-testid="notes-bulk-toggle"
            className={bulkMode ? "bg-[#327de3] hover:bg-[#1f64c2]" : ""}
          >
            <CheckSquare size={14} className="mr-1.5" />
            {bulkMode ? `Cancel (${bulkSelected.size})` : (user?.is_super_admin ? "Bulk pin/delete" : "Bulk pin")}
          </Button>
          {bulkMode && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkSelected.size === 0}
                onClick={() => setBulkAction("pin")}
                data-testid="notes-bulk-pin-button"
              >
                <PushPin size={14} className="mr-1" /> Pin {bulkSelected.size}
              </Button>
              {user?.is_super_admin && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={bulkSelected.size === 0}
                  onClick={() => setBulkAction("delete")}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  data-testid="notes-bulk-delete-button"
                >
                  <Trash size={14} className="mr-1" /> Delete {bulkSelected.size}
                </Button>
              )}
            </>
          )}
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={markAllRead}
              data-testid="notes-mark-all-read"
            >
              <CheckSquare size={14} className="mr-1.5" />
              Mark all as read
            </Button>
            {lastDelete && (
              <button
                type="button"
                onClick={undoLastDelete}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                data-testid="notes-undo-delete-notes"
                title="Restore the most recently deleted note(s)"
              >
                <ClockCounterClockwise size={11} /> Undo delete · {lastDelete.label}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="space-y-2 border-b border-slate-200 pb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <CategoryTab active={tab === ALL_TAB} onClick={() => setTab(ALL_TAB)} testId="notes-tab-all">
            All notes
          </CategoryTab>
          <button
            type="button"
            onClick={() => setEditCatsMode((m) => !m)}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${editCatsMode ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-500 border-slate-200 hover:border-amber-400 hover:text-amber-700"}`}
            data-testid="notes-edit-categories"
          >
            <PencilSimple size={11} /> {editCatsMode ? "Done" : "Edit Categories"}
          </button>
          {tombstones.length > 0 && (
            <button
              type="button"
              onClick={async () => {
                const last = tombstones[0];
                try {
                  await api.post(`/notes/categories/${encodeURIComponent(last.name)}/undo-delete`);
                  toast.success(`Restored "${last.name}"`);
                  loadCategories();
                  loadNotes({ append: false });
                } catch (e) {
                  const m = apiErrorMessage(e); if (m) toast.error(m);
                }
              }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
              data-testid="notes-undo-delete-category"
              title={`Undo delete: ${tombstones[0].name}`}
            >
              <ClockCounterClockwise size={11} /> Undo delete ({tombstones[0].name})
            </button>
          )}
          {categories.map((c) => (
            <CategoryTabWithActions
              key={c.name}
              cat={c}
              active={tab === c.name}
              onClick={() => !editCatsMode && setTab(c.name)}
              editing={editCatsMode}
              onRename={() => setRenamingCat({ name: c.name, newName: c.name })}
              onDelete={() => setConfirmDelCat(c.name)}
              onArchive={async () => {
                try {
                  await api.post(`/notes/categories/${encodeURIComponent(c.name)}/archive`);
                  toast.success(`Archived "${c.name}"`);
                  loadCategories();
                } catch (e) {
                  const m = apiErrorMessage(e); if (m) toast.error(m);
                }
              }}
            />
          ))}
        </div>
        {(archivedCats.length > 0 || showArchived) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-700"
              data-testid="notes-archived-toggle"
            >
              <ClockCounterClockwise size={10} /> Archived ({archivedCats.length}) {showArchived ? "▲" : "▼"}
            </button>
            {showArchived && archivedCats.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={async () => {
                  try {
                    await api.post(`/notes/categories/${encodeURIComponent(c.name)}/unarchive`);
                    toast.success(`Restored "${c.name}"`);
                    loadCategories();
                  } catch (e) {
                    const m = apiErrorMessage(e); if (m) toast.error(m);
                  }
                }}
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100/60 text-slate-500 border-slate-200 hover:bg-slate-200"
                data-testid={`notes-archived-${c.name}`}
                title="Click to unarchive"
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        className={`rounded-2xl border bg-white p-3 sm:p-4 transition-colors ${composerDrag ? "border-[#327de3] bg-[#327de3]/5" : "border-slate-200"}`}
        data-testid="notes-composer"
        onDragOver={(e) => { e.preventDefault(); setComposerDrag(true); }}
        onDragLeave={() => setComposerDrag(false)}
        onDrop={onComposerDrop}
        onPaste={onComposerPaste}
      >
        <RichTextEditor
          value={bodyHtml}
          onChange={(html) => {
            setBodyHtml(html);
            // Strip HTML for the plain-text back-compat body field.
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            setBody(tmp.innerText || tmp.textContent || "");
          }}
          placeholder="Share what you worked on, a feature idea, a question… use #hashtags to tag topics. Paste or drop a file to attach."
          minHeight={110}
          testId="notes-composer-input"
        />
        {pendingAttach && (
          <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 text-xs" data-testid="composer-attachment-chip">
            <Paperclip size={12} className="text-slate-500" />
            <span className="font-medium text-slate-700 truncate flex-1">{pendingAttach.filename}</span>
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">
              {pendingAttach.mode === "preview" ? "Preview" : "Reference"}
            </span>
            <button
              type="button"
              onClick={() => setPendingAttach((p) => ({ ...p, mode: p.mode === "preview" ? "reference" : "preview" }))}
              className="text-[#327de3] hover:text-[#1f64c2] text-[11px] font-semibold"
              data-testid="composer-attachment-toggle-mode"
            >
              Switch
            </button>
            <button
              type="button"
              onClick={() => setPendingAttach(null)}
              className="text-slate-400 hover:text-rose-600"
              data-testid="composer-attachment-remove"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:border-[#327de3] hover:text-[#1f64c2]"
              disabled={uploadingAttach}
              data-testid="composer-attach-button"
              title="Attach a file (max 5 GB, any type)"
            >
              <Paperclip size={12} />
              {uploadingAttach ? "Uploading…" : "Attach"}
            </button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }} />
            <span className="hidden sm:inline">
              <Hash size={12} className="inline" /> Tip: <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px]">⌘ + Enter</kbd> to post, drag a file to attach
            </span>
          </div>
          <Button
            size="sm"
            onClick={post}
            disabled={posting || !body.trim()}
            className="bg-[#327de3] hover:bg-[#1f64c2]"
            data-testid="notes-post-button"
          >
            <PaperPlaneRight size={14} className="mr-1.5" />
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>
        {/* Note group radio bar — post as General / Permissioned / Private */}
        <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-3 text-[11px]" data-testid="composer-group-row">
          <span className="uppercase tracking-widest font-bold text-slate-500">Post as</span>
          <ComposerRadio active={composerGroup === "general"} disabled={!visibleGroups.includes("general")} onClick={() => setComposerGroup("general")} testId="composer-group-general">General</ComposerRadio>
          <ComposerRadio active={composerGroup === "permissioned"} disabled={!visibleGroups.includes("permissioned")} onClick={() => setComposerGroup("permissioned")} testId="composer-group-permissioned">Permissioned</ComposerRadio>
          {canSeePrivate && (
            <ComposerRadio
              active={composerGroup === "private"}
              onClick={() => setComposerGroup("private")}
              testId="composer-group-private"
              tone="private"
              subLabel="super admin access only"
            >
              Private
            </ComposerRadio>
          )}
        </div>
      </div>

      {/* Feed */}
      <div className="space-y-3" data-testid="notes-feed">
        {notes.length === 0 && !loading && (
          <div className="text-center text-sm text-slate-400 py-12">
            No notes yet. Be the first to post.
          </div>
        )}
        {notes.map((n) => {
          const mine = n.author_id === user?.id;
          const isEditing = editingId === n.id;
          const canEdit = mine || user?.is_super_admin;
          const wasEdited = !!n.edited_at && n.edited_at !== n.created_at;
          // "Unread" while the 10-s highlight window is active AND the note is
          // newer than the frozen cursor AND it wasn't authored by the viewer.
          const isUnread = unreadActive && !mine && (!!frozenCursor ? (n.created_at > frozenCursor) : true);
          return (
            <div
              key={n.id}
              data-note-id={n.id}
              className={`flex gap-2 sm:gap-3 transition-colors duration-700 ${mine ? "flex-row-reverse" : ""} ${isUnread ? "bg-amber-50/70 rounded-2xl p-2 -mx-2 ring-1 ring-amber-200" : ""} ${bulkMode && bulkSelected.has(n.id) ? "ring-2 ring-[#327de3] bg-[#327de3]/5 rounded-2xl p-2 -mx-2" : ""} ${highlightId === n.id ? "ring-4 ring-[#327de3] ring-offset-2 bg-[#eaf2ff]/70 rounded-2xl p-2 -mx-2 animate-pulse" : ""}`}
              data-testid={`note-row-${n.id}`}
              data-unread={isUnread ? "true" : undefined}
              onClick={() => {
                if (!bulkMode) return;
                setBulkSelected((prev) => {
                  const next = new Set(prev);
                  if (next.has(n.id)) next.delete(n.id); else next.add(n.id);
                  return next;
                });
              }}
              role={bulkMode ? "button" : undefined}
              style={bulkMode ? { cursor: "pointer" } : undefined}
            >
              {bulkMode && (
                <div className="flex-shrink-0 flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={bulkSelected.has(n.id)}
                    onChange={() => {}}
                    className="w-4 h-4 accent-[#327de3]"
                    data-testid={`note-bulk-checkbox-${n.id}`}
                  />
                </div>
              )}
              <div className="flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-[#327de3] text-white text-xs font-semibold inline-flex items-center justify-center">
                  {initialsFor(n.author_name)}
                </div>
              </div>
              <div className={`max-w-[78%] sm:max-w-[70%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-0.5 px-1 flex-wrap">
                  <span className="font-semibold text-slate-700">{n.author_name || n.author_email}</span>
                  <span>·</span>
                  <span>{fmtTimestamp(n.created_at)}</span>
                  {wasEdited && (
                    <>
                      <span>·</span>
                      <button
                        type="button"
                        onClick={() => setHistoryTarget(n)}
                        className="inline-flex items-center gap-0.5 italic hover:text-[#1f64c2] hover:underline"
                        title={`Edited ${fmtTimestamp(n.edited_at)} by ${n.last_edited_by_name || n.author_name}. Click to view previous versions.`}
                        data-testid={`note-history-${n.id}`}
                      >
                        <ClockCounterClockwise size={11} />
                        edited {fmtTimestamp(n.edited_at)}
                        {n.last_edited_by_name && n.last_edited_by_name !== n.author_name ? ` by ${n.last_edited_by_name}` : ""}
                        <span className="text-[#327de3] ml-0.5">· previous versions</span>
                      </button>
                    </>
                  )}
                </div>
                <div
                  className={`relative rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${
                    mine
                      ? "note-bubble note-bubble-mine bg-[#dbeafe] text-slate-800 rounded-tr-md"
                      : "note-bubble note-bubble-other bg-[#eaf2ff] text-slate-800 rounded-tl-md"
                  } ${isEditing ? "w-full sm:min-w-[360px]" : ""}`}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <RichTextEditor
                        value={editBodyHtml}
                        onChange={(html) => {
                          setEditBodyHtml(html);
                          const tmp = document.createElement("div");
                          tmp.innerHTML = html;
                          setEditBody(tmp.innerText || tmp.textContent || "");
                        }}
                        placeholder="Edit your note…"
                        minHeight={90}
                        testId={`note-edit-input-${n.id}`}
                      />
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEdit}
                          disabled={savingEditId === n.id}
                          data-testid={`note-edit-cancel-${n.id}`}
                        >
                          <X size={13} className="mr-1" />Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveEdit(n)}
                          disabled={savingEditId === n.id || !editBody.trim()}
                          className="bg-[#327de3] hover:bg-[#1f64c2]"
                          data-testid={`note-edit-save-${n.id}`}
                        >
                          <Check size={13} className="mr-1" />
                          {savingEditId === n.id ? "Saving…" : "Save"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="leading-relaxed">{renderBody(n)}</div>
                      {n.attachment && (
                        <NoteAttachmentView
                          attachment={n.attachment}
                          onView={() => setViewAttach(n.attachment)}
                        />
                      )}
                      {n.pinned_category && (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <PushPin size={10} weight="fill" /> {n.pinned_category}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400 px-1">
                    {canEdit && (
                      <>
                        <button onClick={() => startEdit(n)} className="hover:text-[#1f64c2] inline-flex items-center gap-1" data-testid={`note-edit-${n.id}`}>
                          <PencilSimple size={12} /> Edit
                        </button>
                        <span>·</span>
                      </>
                    )}
                    {n.pinned_category ? (
                      <button onClick={() => onUnpin(n)} className="hover:text-slate-700 inline-flex items-center gap-1" data-testid={`note-unpin-${n.id}`}>
                        <PushPinSlash size={12} /> Unpin
                      </button>
                    ) : (
                      <button onClick={() => onPin(n)} className="hover:text-amber-700 inline-flex items-center gap-1" data-testid={`note-pin-${n.id}`}>
                        <PushPin size={12} /> Pin
                      </button>
                    )}
                    {(mine || user?.is_super_admin) && (
                      <>
                        <span>·</span>
                        <button onClick={() => onDelete(n)} className="hover:text-rose-600 inline-flex items-center gap-1" data-testid={`note-delete-${n.id}`}>
                          <Trash size={12} /> Delete
                        </button>
                      </>
                    )}
                    {(mine || user?.is_super_admin) && (
                      <>
                        <span>·</span>
                        <button
                          onClick={() => setGroupChangeTarget(n)}
                          className="hover:text-[#1f64c2] inline-flex items-center gap-1"
                          data-testid={`permissions_button_existing_note-${n.id}`}
                        >
                          <Hash size={12} /> Permissions
                        </button>
                      </>
                    )}
                    {activeHashtag && (
                      <>
                        <span>·</span>
                        <button
                          onClick={() => jumpToNote(n)}
                          className="hover:text-[#1f64c2] inline-flex items-center gap-1 font-semibold text-[#1f64c2]"
                          data-testid={`note-jump-to-feed-${n.id}`}
                          title="Clear the search filter and view this note in the full feed"
                        >
                          <MagnifyingGlass size={12} /> Jump to feed
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="text-center text-xs text-slate-400 py-3">Loading…</div>
        )}
        {hasMore && <div ref={sentinelRef} className="h-1" />}
      </div>
      </>
      )}
      </>
      )}

      <PinModal
        target={pinTarget}
        categories={categories}
        onClose={() => setPinTarget(null)}
        onPinned={(updated) => {
          setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
          loadCategories();
        }}
      />

      <HistoryModal target={historyTarget} onClose={() => setHistoryTarget(null)} />

      <AttachModeChooser
        file={attachModalFile}
        onPick={(mode) => {
          setPendingAttach({ ...attachModalFile, mode });
          setAttachModalFile(null);
        }}
        onClose={() => setAttachModalFile(null)}
      />

      <AttachViewerModal
        attachment={viewAttach}
        onClose={() => setViewAttach(null)}
      />

      <BulkActionModal
        action={bulkAction}
        count={bulkSelected.size}
        categories={categories}
        onClose={() => setBulkAction(null)}
        onConfirm={async (payload) => {
          try {
            if (bulkAction === "delete") {
              const ids = Array.from(bulkSelected);
              await api.post("/notes/bulk/delete", { note_ids: ids });
              setNotes((prev) => prev.filter((n) => !bulkSelected.has(n.id)));
              setLastDelete({ ids, label: `${ids.length} note${ids.length === 1 ? "" : "s"} deleted` });
              toast.success(`Deleted ${ids.length} note${ids.length === 1 ? "" : "s"}`);
            } else if (bulkAction === "pin") {
              await api.post("/notes/bulk/pin", { note_ids: Array.from(bulkSelected), category: payload.category });
              setNotes((prev) => prev.map((n) => (bulkSelected.has(n.id) ? { ...n, pinned_category: payload.category } : n)));
              loadCategories();
              toast.success(`Pinned ${bulkSelected.size} note${bulkSelected.size === 1 ? "" : "s"} to ${payload.category}`);
            }
          } catch (e) {
            const m = apiErrorMessage(e); if (m) toast.error(m);
          } finally {
            setBulkAction(null);
            setBulkMode(false);
            setBulkSelected(new Set());
          }
        }}
      />

      <RenameCategoryModal
        target={renamingCat}
        onClose={() => setRenamingCat(null)}
        onSubmit={async (newName) => {
          try {
            await api.post(`/notes/categories/${encodeURIComponent(renamingCat.name)}/rename`, { new_name: newName });
            toast.success(`Renamed to "${newName}"`);
            setRenamingCat(null);
            loadCategories();
            // Refresh notes so pinned_category updates inline.
            setNotes((prev) => prev.map((n) => (n.pinned_category === renamingCat.name ? { ...n, pinned_category: newName } : n)));
            if (tab === renamingCat.name) setTab(newName);
          } catch (e) {
            const m = apiErrorMessage(e); if (m) toast.error(m);
          }
        }}
      />

      <ConfirmDeleteCategoryModal
        name={confirmDelCat}
        onClose={() => setConfirmDelCat(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/notes/categories/${encodeURIComponent(confirmDelCat)}`);
            toast.success(`Deleted "${confirmDelCat}" — notes kept under All notes. Undo button is available.`);
            // Drop pinned_category from any visible notes
            setNotes((prev) => prev.map((n) => (n.pinned_category === confirmDelCat ? { ...n, pinned_category: null } : n)));
            if (tab === confirmDelCat) setTab(ALL_TAB);
            setConfirmDelCat(null);
            loadCategories();
          } catch (e) {
            const m = apiErrorMessage(e); if (m) toast.error(m);
          }
        }}
      />

      <Dialog
        open={!!confirmDeleteNote}
        onOpenChange={(o) => !o && setConfirmDeleteNote(null)}
      >
        <DialogContent data-testid="confirm-delete-note-modal">
          <DialogHeader>
            <DialogTitle>Delete this chat bubble?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete this chat bubble? You can undo this from the
            <strong> Undo delete</strong> button that appears under <em>Mark all as read</em>
            (it persists until you refresh the page or click it).
          </p>
          {confirmDeleteNote && (
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 line-clamp-3">
              {confirmDeleteNote.body}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteNote(null)}>Cancel</Button>
            <Button
              onClick={confirmIndividualDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-note-confirm"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NoteGroupModal
        target={groupChangeTarget}
        canSeePrivate={canSeePrivate}
        visibleGroups={visibleGroups}
        onClose={() => setGroupChangeTarget(null)}
        onChange={changeNoteGroup}
      />
    </div>
  );
}

function GroupPill({ active, onClick, children, testId, tone }) {
  const activeCls = tone === "private"
    ? "bg-rose-600 text-white shadow-sm"
    : "bg-[#1f64c2] text-white shadow-sm";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
        active ? activeCls : "bg-white border border-slate-200 text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function ComposerRadio({ active, disabled, onClick, children, testId, tone, subLabel }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border transition-colors ${
        active
          ? (tone === "private" ? "bg-rose-50 border-rose-300 text-rose-800" : "bg-[#eaf2ff] border-[#327de3] text-[#1f64c2]")
          : disabled
            ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
            : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
      }`}
    >
      <span className={`w-2.5 h-2.5 rounded-full border ${active ? (tone === "private" ? "bg-rose-500 border-rose-500" : "bg-[#327de3] border-[#327de3]") : "bg-white border-slate-300"}`} />
      <span className="font-semibold">{children}</span>
      {subLabel ? <span className="text-[9px] text-rose-500 italic normal-case font-normal">({subLabel})</span> : null}
    </button>
  );
}

function NoteGroupModal({ target, canSeePrivate, visibleGroups, onClose, onChange }) {
  const [pick, setPick] = useState(target?.group || "general");
  useEffect(() => { setPick(target?.group || "general"); }, [target?.id, target?.group]);
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="note-group-modal">
        <DialogHeader>
          <DialogTitle>Reassign note</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3 italic line-clamp-3">
          &ldquo;{target.body}&rdquo;
        </div>
        <div className="space-y-2">
          <GroupRow value="general" active={pick === "general"} disabled={!visibleGroups.includes("general")} onPick={() => setPick("general")} label="General notes" hint="Everyone with notes access can see these." />
          <GroupRow value="permissioned" active={pick === "permissioned"} disabled={!visibleGroups.includes("permissioned")} onPick={() => setPick("permissioned")} label="Permissioned notes" hint="Only users granted permissioned-notes access." />
          {canSeePrivate && (
            <GroupRow value="private" active={pick === "private"} onPick={() => setPick("private")} label="Private notes" hint="Super admin access only." tone="private" />
          )}
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="note-group-cancel">Cancel</Button>
          <Button
            onClick={() => onChange(target.id, pick)}
            disabled={pick === (target.group || "general")}
            className="bg-[#1f64c2] hover:bg-[#164a97] text-white"
            data-testid="note-group-save"
          >
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupRow({ value, active, disabled, onPick, label, hint, tone }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onPick}
      disabled={disabled}
      data-testid={`note-group-option-${value}`}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        active
          ? (tone === "private" ? "border-rose-400 bg-rose-50" : "border-[#327de3] bg-[#eaf2ff]")
          : disabled
            ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
            : "border-slate-200 bg-white hover:border-slate-400"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-3 h-3 rounded-full border-2 ${active ? (tone === "private" ? "bg-rose-500 border-rose-500" : "bg-[#327de3] border-[#327de3]") : "border-slate-300"}`} />
        <span className="text-sm font-bold text-slate-800">{label}</span>
      </div>
      <div className={`text-[11px] ml-5 mt-0.5 ${tone === "private" ? "text-rose-600" : "text-slate-500"}`}>{hint}</div>
    </button>
  );
}

function RenameCategoryModal({ target, onClose, onSubmit }) {
  const [val, setVal] = useState("");
  useEffect(() => { if (target) setVal(target.name); }, [target]);
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="category-rename-modal">
        <DialogHeader>
          <DialogTitle>Rename category &quot;{target.name}&quot;</DialogTitle>
        </DialogHeader>
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          maxLength={60}
          autoFocus
          data-testid="category-rename-input"
        />
        <p className="text-[11px] text-slate-500">
          This renames the category everywhere it appears — on the tab strip and on every chat bubble pinned to it.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSubmit(val.trim())}
            disabled={!val.trim() || val.trim() === target.name}
            className="bg-[#327de3] hover:bg-[#1f64c2]"
            data-testid="category-rename-submit"
          >
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteCategoryModal({ name, onClose, onConfirm }) {
  if (!name) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="category-delete-modal">
        <DialogHeader>
          <DialogTitle>Delete category &quot;{name}&quot;?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          The category will be removed, but the chats pinned to it will stay visible under
          <strong> All notes</strong> — they just lose their category reference. You can use the
          <strong> Undo delete</strong> button at the top of the page to restore both the category and its pin links.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="category-delete-confirm">
            Delete category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkActionModal({ action, count, categories, onClose, onConfirm }) {
  const [mode, setMode] = useState("existing");
  const [existing, setExisting] = useState("");
  const [fresh, setFresh] = useState("");
  useEffect(() => {
    if (action === "pin") {
      setMode(categories.length ? "existing" : "new");
      setExisting(categories[0]?.name || "");
      setFresh("");
    }
  }, [action, categories]);
  if (!action) return null;
  if (action === "delete") {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle>Delete {count} note{count === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes the selected notes and their version history.
            The action will be logged in the Log page.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => onConfirm({})}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="bulk-delete-confirm"
            >
              Delete {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  // pin
  const submit = () => {
    const cat = (mode === "existing" ? existing : fresh).trim();
    if (!cat) { toast.error("Pick or enter a category"); return; }
    onConfirm({ category: cat });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-pin-modal">
        <DialogHeader>
          <DialogTitle>Pin {count} note{count === 1 ? "" : "s"} to a category</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("existing")}
              disabled={!categories.length}
              className={`flex-1 text-xs font-semibold py-2 rounded-md border ${mode === "existing" ? "bg-[#327de3] text-white border-[#327de3]" : "bg-white text-slate-600 border-slate-200"} ${!categories.length ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Existing category
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              className={`flex-1 text-xs font-semibold py-2 rounded-md border ${mode === "new" ? "bg-[#327de3] text-white border-[#327de3]" : "bg-white text-slate-600 border-slate-200"}`}
            >
              + New category
            </button>
          </div>
          {mode === "existing" ? (
            <Select value={existing} onValueChange={setExisting}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={fresh}
              onChange={(e) => setFresh(e.target.value)}
              placeholder="e.g. Release notes, Bugs, Ideas"
              maxLength={60}
              data-testid="bulk-pin-new-input"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="bulk-pin-confirm">
            Pin {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function humanSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function attachUrl(a, dl = false) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/notes/attachments/${a.file_id}${dl ? "?download=true" : ""}`;
}

function NoteAttachmentView({ attachment, onView }) {
  const isPdf = (attachment.content_type || "").toLowerCase().includes("pdf") || (attachment.filename || "").toLowerCase().endsWith(".pdf");
  const isImg = (attachment.content_type || "").toLowerCase().startsWith("image/");
  if (attachment.mode === "preview") {
    return (
      <div className="mt-2 rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="note-attachment-preview">
        {isPdf ? (
          <iframe title={attachment.filename} src={attachUrl(attachment)} className="w-full h-[320px] border-0 bg-white" />
        ) : isImg ? (
          <img src={attachUrl(attachment)} alt={attachment.filename} className="w-full max-h-[320px] object-contain bg-slate-50" />
        ) : (
          <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-500">
            <FilePdf size={18} className="text-slate-400" /> Preview not available for this file type.
          </div>
        )}
        <div className="px-2 py-1.5 flex items-center gap-3 text-[11px] border-t border-slate-200 bg-slate-50">
          <span className="font-medium text-slate-700 truncate flex-1">{attachment.filename}</span>
          <button type="button" onClick={onView} className="inline-flex items-center gap-0.5 text-[#327de3] hover:text-[#1f64c2]">
            <Eye size={11} /> view
          </button>
          <a href={attachUrl(attachment, true)} className="inline-flex items-center gap-0.5 text-[#327de3] hover:text-[#1f64c2]">
            <Download size={11} /> download
          </a>
        </div>
      </div>
    );
  }
  // 'reference' mode: small paperclip + filename + view/download
  return (
    <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-600 bg-white/60 border border-slate-200 rounded-md px-2 py-1 max-w-full" data-testid="note-attachment-reference">
      <Paperclip size={11} className="text-slate-500 shrink-0" />
      <span className="truncate font-medium" title={attachment.filename}>{attachment.filename}</span>
      <span className="text-slate-400">·</span>
      <button type="button" onClick={onView} className="inline-flex items-center gap-0.5 text-[#327de3] hover:text-[#1f64c2]">
        <Eye size={10} /> view
      </button>
      <a href={attachUrl(attachment, true)} className="inline-flex items-center gap-0.5 text-[#327de3] hover:text-[#1f64c2]">
        <Download size={10} /> download
      </a>
    </div>
  );
}

function AttachModeChooser({ file, onPick, onClose }) {
  const [mode, setMode] = useState("reference");
  useEffect(() => { if (file) setMode("reference"); }, [file]);
  if (!file) return null;
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="attach-mode-chooser">
        <DialogHeader>
          <DialogTitle>How should this attachment be shown?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="font-medium truncate">{file.filename}</div>
            <div className="text-[11px] text-slate-500">{humanSize(file.size)} · {file.content_type}</div>
          </div>
          <label className={`flex gap-3 items-start rounded-lg border px-3 py-2 cursor-pointer ${mode === "preview" ? "border-[#327de3] bg-[#327de3]/5" : "border-slate-200"}`} data-testid="attach-mode-preview">
            <input type="radio" className="mt-1" name="atm" checked={mode === "preview"} onChange={() => setMode("preview")} />
            <div>
              <div className="text-sm font-semibold">Preview inline</div>
              <div className="text-[11px] text-slate-500">Render the file (PDFs, images) right inside the chat bubble.</div>
            </div>
          </label>
          <label className={`flex gap-3 items-start rounded-lg border px-3 py-2 cursor-pointer ${mode === "reference" ? "border-[#327de3] bg-[#327de3]/5" : "border-slate-200"}`} data-testid="attach-mode-reference">
            <input type="radio" className="mt-1" name="atm" checked={mode === "reference"} onChange={() => setMode("reference")} />
            <div>
              <div className="text-sm font-semibold">Reference link</div>
              <div className="text-[11px] text-slate-500">Small paperclip + filename — view / download links underneath.</div>
            </div>
          </label>
          <div className="text-[11px] text-slate-400">You can switch between Preview and Reference later by editing the post.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onPick(mode)} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="attach-mode-confirm">
            Use this {mode === "preview" ? "preview" : "reference"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachViewerModal({ attachment, onClose }) {
  if (!attachment) return null;
  const isPdf = (attachment.content_type || "").toLowerCase().includes("pdf") || (attachment.filename || "").toLowerCase().endsWith(".pdf");
  const isImg = (attachment.content_type || "").toLowerCase().startsWith("image/");
  return (
    <Dialog open={!!attachment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="note-attachment-viewer">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 flex flex-row items-center justify-between">
          <div className="flex flex-col gap-0.5 min-w-0">
            <DialogTitle className="text-sm font-semibold truncate">{attachment.filename}</DialogTitle>
            <div className="text-[11px] text-slate-500">{humanSize(attachment.size)} · {attachment.content_type}</div>
          </div>
          <a href={attachUrl(attachment, true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#327de3] hover:text-[#1f64c2] mr-6">
            <Download size={14} /> Download
          </a>
        </DialogHeader>
        <div className="h-[75vh] bg-slate-100">
          {isPdf ? (
            <iframe title={attachment.filename} src={attachUrl(attachment)} className="w-full h-full border-0" />
          ) : isImg ? (
            <img src={attachUrl(attachment)} alt={attachment.filename} className="w-full h-full object-contain bg-white" />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-3">
              <Paperclip size={32} className="text-slate-300" />
              <div>Inline preview isn&apos;t available for this file type.</div>
              <a href={attachUrl(attachment, true)} className="text-[#327de3] hover:text-[#1f64c2] font-semibold">Download to view</a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryModal({ target, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) { setData(null); return; }
    setLoading(true);
    api.get(`/notes/${target.id}/history`)
      .then((r) => setData(r.data))
      .catch((e) => { const m = apiErrorMessage(e); if (m) toast.error(m); })
      .finally(() => setLoading(false));
  }, [target]);

  if (!target) return null;

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="note-history-modal">
        <DialogHeader>
          <DialogTitle>Previous versions</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-center text-sm text-slate-400 py-6">Loading…</div>}
        {!loading && data && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {/* Current */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="flex items-center justify-between text-[11px] text-emerald-700 font-semibold mb-1">
                <span>CURRENT</span>
                <span className="text-slate-500 font-normal">
                  {fmtTimestamp(data.current.edited_at)}
                  {data.current.edited_by_name ? ` · ${data.current.edited_by_name}` : ""}
                </span>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.current.body}</div>
            </div>
            {/* History */}
            {data.versions.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-4">No previous versions.</div>
            ) : (
              data.versions.map((v, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`note-history-version-${idx}`}>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 font-semibold mb-1">
                    <span>VERSION {data.versions.length - idx}</span>
                    <span className="text-slate-500 font-normal">
                      {fmtTimestamp(v.edited_at)}
                      {v.edited_by_name ? ` · ${v.edited_by_name}` : ""}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{v.body}</div>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CategoryTabWithActions({ cat, active, onClick, editing, onRename, onDelete, onArchive }) {
  if (!editing) {
    return (
      <CategoryTab active={active} onClick={onClick} testId={`notes-tab-${cat.name}`} icon={<PushPin size={12} weight="fill" className="text-amber-500" />}>
        {cat.name}
      </CategoryTab>
    );
  }
  return (
    <div className="inline-flex flex-col items-stretch" data-testid={`category-edit-${cat.name}`}>
      <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-amber-50 text-amber-700 border-amber-300">
        <PushPin size={12} weight="fill" className="text-amber-500" />
        {cat.name}
      </div>
      <div className="flex items-center gap-2 justify-center mt-0.5 text-[10px]">
        <button onClick={onRename} className="text-[#327de3] hover:underline" data-testid={`category-rename-${cat.name}`}>rename</button>
        <span className="text-slate-300">·</span>
        <button onClick={onDelete} className="text-rose-600 hover:underline" data-testid={`category-delete-${cat.name}`}>delete</button>
        <span className="text-slate-300">·</span>
        <button onClick={onArchive} className="text-slate-500 hover:underline" data-testid={`category-archive-${cat.name}`}>archive</button>
      </div>
    </div>
  );
}

function CategoryTab({ active, onClick, children, testId, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-[#327de3] text-white border-[#327de3]"
          : "bg-white text-slate-600 border-slate-200 hover:border-[#327de3] hover:text-[#1f64c2]"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function PinModal({ target, categories, onClose, onPinned }) {
  const [mode, setMode] = useState("existing"); // 'existing' | 'new'
  const [existing, setExisting] = useState("");
  const [fresh, setFresh] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setMode(categories.length ? "existing" : "new");
      setExisting(categories[0]?.name || "");
      setFresh("");
    }
  }, [target, categories]);

  if (!target) return null;

  const submit = async () => {
    const cat = (mode === "existing" ? existing : fresh).trim();
    if (!cat) {
      toast.error("Pick or enter a category");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/notes/${target.id}/pin`, { category: cat });
      onPinned({ ...target, pinned_category: cat });
      toast.success(`Pinned to ${cat}`);
      onClose();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="note-pin-modal">
        <DialogHeader>
          <DialogTitle>Pin note to a category</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-slate-500 italic line-clamp-3 bg-slate-50 border border-slate-200 rounded-md p-2">
            {target.body}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("existing")}
              disabled={!categories.length}
              className={`flex-1 text-xs font-semibold py-2 rounded-md border ${
                mode === "existing" ? "bg-[#327de3] text-white border-[#327de3]" : "bg-white text-slate-600 border-slate-200"
              } ${!categories.length ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="pin-mode-existing"
            >
              Existing category
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              className={`flex-1 text-xs font-semibold py-2 rounded-md border ${
                mode === "new" ? "bg-[#327de3] text-white border-[#327de3]" : "bg-white text-slate-600 border-slate-200"
              }`}
              data-testid="pin-mode-new"
            >
              + New category
            </button>
          </div>

          {mode === "existing" ? (
            <Select value={existing} onValueChange={setExisting}>
              <SelectTrigger data-testid="pin-existing-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              autoFocus
              value={fresh}
              onChange={(e) => setFresh(e.target.value)}
              placeholder="e.g. Release notes, Bugs, Ideas"
              maxLength={60}
              data-testid="pin-new-input"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="pin-submit-button">
            {busy ? "Pinning…" : "Pin note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
